import { CONTRACT_APPROVAL_TYPES } from '@/constant';
import type { ContractApprovalAttributes } from '@/types/approval';

/**
 * 계약 품의(117·118) attributes.contractIds 변환.
 * 서버 계약: 쉼표 결합 문자열, 결합값 최대 100자(5자리 ID 기준 16건). 초과는 저장·draft-source 모두 422.
 */
export const CONTRACT_IDS_MAX_LENGTH = 100;

/** 117·118 — 작성 폼·저장 attributes·조회 슬롯을 한 구현이 공유하는 판정 */
export const isContractApprovalType = (apvType: number): boolean =>
  CONTRACT_APPROVAL_TYPES.includes(apvType);

/**
 * "48213,48219" → [48213, 48219]. 빈 값·숫자 아닌 토큰은 버리고 순서 유지.
 * 단건조회 attributes 는 Record<string, unknown> 이라 문자열 아닌 값도 그대로 받아 빈 배열로 처리
 */
export const parseContractIds = (value: unknown): number[] => {
  if (typeof value !== 'string' || !value) return [];
  return value
    .split(',')
    .map((token) => token.trim())
    .filter((token) => /^\d+$/.test(token))
    .map(Number);
};

/**
 * 법틀 → 작성 화면 진입 쿼리 키(앞이 우선). contract_id 는 이 페이지 관례(apv_type 와 같은 snake_case),
 * Gwdocno 는 법틀이 붙이는 키, Ep 는 레거시 create 의 키 —
 * 법틀이 호스트·경로만 바꿔도 열리게 모두 받음
 */
export const CONTRACT_ENTRY_QUERY_KEYS = [
  'contract_id',
  'Gwdocno',
  'Ep',
] as const;

/** 진입 쿼리 → 첫 등록분 계약 ID. 쉼표 다건·반복 키 모두 허용, 중복 제거·순서 유지. 없으면 빈 배열 */
export const getEntryContractIds = (
  query: Record<string, string | string[] | undefined>,
): number[] => {
  for (const key of CONTRACT_ENTRY_QUERY_KEYS) {
    const raw = query[key];
    const ids = parseContractIds(Array.isArray(raw) ? raw.join(',') : raw);
    if (ids.length) return Array.from(new Set(ids));
  }
  return [];
};

/** [48213, 48219] → "48213,48219". 빈 배열은 null — 서버 nullable 필드와 맞춤 */
export const serializeContractIds = (ids: number[]): string | null =>
  ids.length ? ids.join(',') : null;

/** 결합 문자열이 서버 한도(100자) 안인지. 카드 추가 전 게이팅용 */
export const isContractIdsWithinLimit = (ids: number[]): boolean =>
  (serializeContractIds(ids) ?? '').length <= CONTRACT_IDS_MAX_LENGTH;

/**
 * 저장(POST/PUT) attributes. content 는 서버 필수라 빈 본문도 '' 로 채움 — 비어 있으면 서버 422 가 안내.
 * zsum·approvalLink·contractData 는 여기서 만들지 않음(서버가 채우거나 무시, contractData 는 보내면 422)
 */
export const buildContractAttributes = (
  content: string | null,
  contractIds: number[],
  contractContent: string | null = null,
): ContractApprovalAttributes => ({
  content: content ?? '',
  contractIds: serializeContractIds(contractIds),
  contractContent: contractIds.length ? contractContent : null,
});
